/**
 * Indicator catalog metadata (parameters, names, pane placement).
 * Engine implementations live in chart-helpers/indicators-KLine.
 */

export type IndicatorCategoryId =
  | "trend"
  | "oscillators"
  | "volumes"
  | "bill-williams"
  | "custom";

export interface CatalogParameter {
  name: string;
  label?: string;
  type: "number" | "color" | "select";
  defaultValue: number | string;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
}

export interface CatalogEntry {
  indicatorId: string;
  name: string;
  category: IndicatorCategoryId;
  /** true = separate pane below main chart */
  createsNewChart: boolean;
  parameters: CatalogParameter[];
}

const trendIndicators: CatalogEntry[] = [
  {
    indicatorId: "adx",
    name: "Average Directional Movement Index",
    createsNewChart: true,
    category: "trend",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "bollinger-bands",
    name: "Bollinger Bands",
    createsNewChart: false,
    category: "trend",
    parameters: [
      { name: "Period", type: "number", defaultValue: 20, min: 1, max: 100 },
      { name: "Shift", type: "number", defaultValue: 0, min: -100, max: 100 },
      {
        name: "Deviations",
        type: "number",
        defaultValue: 2.0,
        min: 0.1,
        max: 5,
        step: 0.001,
      },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "envelopes",
    name: "Envelopes",
    createsNewChart: false,
    category: "trend",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
      { name: "Shift", type: "number", defaultValue: 0, min: -100, max: 100 },
      {
        name: "Deviation",
        type: "number",
        defaultValue: 0.1,
        min: 0.001,
        max: 10,
        step: 0.001,
      },
      {
        name: "Method",
        type: "select",
        defaultValue: "Simple",
        options: ["Simple", "Exponential", "Smoothed", "Linear Weighted"],
      },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "ichimoku",
    name: "Ichimoku Kinko Hyo",
    createsNewChart: false,
    category: "trend",
    parameters: [
      { name: "Tenkan-sen", type: "number", defaultValue: 9, min: 1, max: 100 },
      { name: "Kijun-sen", type: "number", defaultValue: 26, min: 1, max: 100 },
      {
        name: "Senkou Span B",
        type: "number",
        defaultValue: 52,
        min: 1,
        max: 200,
      },
    ],
  },
  {
    indicatorId: "ma",
    name: "Moving Average",
    createsNewChart: false,
    category: "trend",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 200 },
      { name: "Shift", type: "number", defaultValue: 0, min: -100, max: 100 },
      {
        name: "Method",
        type: "select",
        defaultValue: "Simple",
        options: ["Simple", "Exponential", "Smoothed", "Linear Weighted"],
      },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "parabolic-sar",
    name: "Parabolic SAR",
    createsNewChart: false,
    category: "trend",
    parameters: [
      { name: "Step", type: "number", defaultValue: 0.02, min: 0.001, max: 1 },
      { name: "Maximum", type: "number", defaultValue: 0.2, min: 0.01, max: 1 },
    ],
  },
  {
    indicatorId: "std-dev",
    name: "Standard Deviation",
    createsNewChart: true,
    category: "trend",
    parameters: [
      { name: "Period", type: "number", defaultValue: 20, min: 1, max: 100 },
      { name: "Shift", type: "number", defaultValue: 0, min: -100, max: 100 },
      {
        name: "Method",
        type: "select",
        defaultValue: "Simple",
        options: ["Simple", "Exponential"],
      },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
];

const oscillatorIndicators: CatalogEntry[] = [
  {
    indicatorId: "atr",
    name: "Average True Range",
    category: "oscillators",
    createsNewChart: true,
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "bears-power",
    name: "Bears Power",
    category: "oscillators",
    createsNewChart: true,
    parameters: [
      { name: "Period", type: "number", defaultValue: 13, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "bulls-power",
    name: "Bulls Power",
    category: "oscillators",
    createsNewChart: true,
    parameters: [
      { name: "Period", type: "number", defaultValue: 13, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "cci",
    name: "Commodity Channel Index",
    category: "oscillators",
    createsNewChart: true,
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Typical Price (HLC/3)",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "demarker",
    name: "DeMarker",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "force-index",
    name: "Force Index",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Period", type: "number", defaultValue: 13, min: 1, max: 100 },
      {
        name: "Method",
        type: "select",
        defaultValue: "Simple",
        options: ["Simple", "Exponential"],
      },
    ],
  },
  {
    indicatorId: "macd",
    name: "MACD",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Fast EMA", type: "number", defaultValue: 12, min: 1, max: 100 },
      { name: "Slow EMA", type: "number", defaultValue: 26, min: 1, max: 100 },
      { name: "MACD SMA", type: "number", defaultValue: 9, min: 1, max: 100 },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "momentum",
    name: "Momentum",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "osma",
    name: "Moving Average of Oscillator",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Fast EMA", type: "number", defaultValue: 12, min: 1, max: 100 },
      { name: "Slow EMA", type: "number", defaultValue: 26, min: 1, max: 100 },
      { name: "MACD SMA", type: "number", defaultValue: 9, min: 1, max: 100 },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "rsi",
    name: "Relative Strength Index",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
      {
        name: "Apply to",
        type: "select",
        defaultValue: "Close",
        options: [
          "Close",
          "Open",
          "High",
          "Low",
          "Median Price (HL/2)",
          "Typical Price (HLC/3)",
          "Weighted Close (HLCC/4)",
        ],
      },
    ],
  },
  {
    indicatorId: "rvi",
    name: "Relative Vigor Index",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Period", type: "number", defaultValue: 10, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "stochastic",
    name: "Stochastic Oscillator",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "%K Period", type: "number", defaultValue: 5, min: 1, max: 100 },
      { name: "%D Period", type: "number", defaultValue: 3, min: 1, max: 100 },
      { name: "Slowing", type: "number", defaultValue: 3, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "wpr",
    name: "Williams' Percent Range",
    createsNewChart: true,
    category: "oscillators",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
    ],
  },
];

const volumeIndicators: CatalogEntry[] = [
  {
    indicatorId: "ad",
    name: "Accumulation/Distribution",
    createsNewChart: true,
    category: "volumes",
    parameters: [],
  },
  {
    indicatorId: "mfi",
    name: "Money Flow Index",
    createsNewChart: true,
    category: "volumes",
    parameters: [
      { name: "Period", type: "number", defaultValue: 14, min: 1, max: 100 },
    ],
  },
  {
    indicatorId: "obv",
    name: "On Balance Volume",
    createsNewChart: true,
    category: "volumes",
    parameters: [],
  },
  {
    indicatorId: "volumes",
    name: "Volumes",
    createsNewChart: true,
    category: "volumes",
    parameters: [],
  },
];

const billWilliamsIndicators: CatalogEntry[] = [
  {
    indicatorId: "ac",
    name: "Accelerator Oscillator",
    createsNewChart: true,
    category: "bill-williams",
    parameters: [],
  },
  {
    indicatorId: "alligator",
    name: "Alligator",
    createsNewChart: false,
    category: "bill-williams",
    parameters: [
      {
        name: "Jaw Period",
        type: "number",
        defaultValue: 13,
        min: 1,
        max: 100,
      },
      { name: "Jaw Shift", type: "number", defaultValue: 8, min: 0, max: 100 },
      {
        name: "Teeth Period",
        type: "number",
        defaultValue: 8,
        min: 1,
        max: 100,
      },
      {
        name: "Teeth Shift",
        type: "number",
        defaultValue: 5,
        min: 0,
        max: 100,
      },
      {
        name: "Lips Period",
        type: "number",
        defaultValue: 5,
        min: 1,
        max: 100,
      },
      { name: "Lips Shift", type: "number", defaultValue: 3, min: 0, max: 100 },
    ],
  },
  {
    indicatorId: "ao",
    name: "Awesome Oscillator",
    createsNewChart: true,
    category: "bill-williams",
    parameters: [],
  },
  {
    indicatorId: "fractals",
    name: "Fractals",
    createsNewChart: false,
    category: "bill-williams",
    parameters: [],
  },
  {
    indicatorId: "gator",
    name: "Gator Oscillator",
    createsNewChart: true,
    category: "bill-williams",
    parameters: [
      {
        name: "Jaw Period",
        type: "number",
        defaultValue: 13,
        min: 1,
        max: 100,
      },
      { name: "Jaw Shift", type: "number", defaultValue: 8, min: 0, max: 100 },
      {
        name: "Teeth Period",
        type: "number",
        defaultValue: 8,
        min: 1,
        max: 100,
      },
      {
        name: "Teeth Shift",
        type: "number",
        defaultValue: 5,
        min: 0,
        max: 100,
      },
      {
        name: "Lips Period",
        type: "number",
        defaultValue: 5,
        min: 1,
        max: 100,
      },
      { name: "Lips Shift", type: "number", defaultValue: 3, min: 0, max: 100 },
    ],
  },
  {
    indicatorId: "mfi-bw",
    name: "Market Facilitation Index",
    createsNewChart: true,
    category: "bill-williams",
    parameters: [],
  },
];

export const indicatorCatalog: CatalogEntry[] = [
  ...trendIndicators,
  ...oscillatorIndicators,
  ...volumeIndicators,
  ...billWilliamsIndicators,
];

export const indicatorCatalogById: Record<string, CatalogEntry> =
  Object.fromEntries(indicatorCatalog.map((e) => [e.indicatorId, e]));

export const CATEGORY_LABELS: Record<IndicatorCategoryId, string> = {
  trend: "TREND",
  oscillators: "OSCILLATORS",
  volumes: "VOLUMES",
  "bill-williams": "BILL WILLIAMS",
  custom: "CUSTOM",
};
